import React, { useState, useEffect, useRef } from 'react';
import './TabAudioTranscription.css';

// Controls component for starting/stopping tab audio capture
function Controls({ isCapturing, onStartCapture, onStopCapture }) {
  return (
    <div className="tab-transcription-controls">
      <button
        className={`control-button ${isCapturing ? 'capturing' : ''}`}
        onClick={isCapturing ? onStopCapture : onStartCapture}
      >
        {isCapturing ? 'Stop Capturing' : 'Capture Tab Audio'}
      </button>
      <p className="help-text">
        {isCapturing 
          ? 'Capturing and transcribing tab audio...' 
          : 'Click to capture audio from a YouTube, Google Meet, or other tab'}
      </p>
    </div>
  );
}

// Live captions component
function LiveCaptions({ interimText }) {
  if (!interimText) return null;
  return (
    <div className="live-captions">
      <div className="caption-text">{interimText}</div>
    </div>
  );
}

// Chat component for displaying AI responses
function ChatDisplay({ messages }) {
  const chatRef = useRef(null);
  
  useEffect(() => {
    // Auto-scroll to bottom when new messages are added
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);
  
  return (
    <div className="chat-container">
      <div className="chat-header">
        <h2>AI Assistant</h2>
      </div>
      <div className="chat-messages" ref={chatRef}>
        {messages.length === 0 && (
          <p className="empty-state">AI responses will appear here when questions are detected in the tab audio.</p>
        )}
        {messages.map((message, index) => (
          <div key={index} className={`chat-message ${message.role}`}>
            <div className="message-content">
              {message.isLoading ? (
                <div className="loading-dots">
                  <span></span><span></span><span></span>
                </div>
              ) : (
                message.content
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Transcript component
function Transcript({ transcriptData }) {
  const transcriptRef = useRef(null);

  useEffect(() => {
    // Auto-scroll to bottom when new entries are added
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcriptData]);

  const downloadTranscript = () => {
    let content = "Tab Audio Transcript:\n\n";
    transcriptData.forEach(entry => {
      content += `[${entry.timestamp.toLocaleTimeString()}] ${entry.text}\n`;
    });
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tab-transcript.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="transcript-container">
      <div className="transcript-header">
        <h2>Tab Audio Transcript</h2>
        {transcriptData.length > 0 && (
          <button onClick={downloadTranscript} className="download-button">
            Download Transcript
          </button>
        )}
      </div>
      <div className="transcript-content" ref={transcriptRef}>
        {transcriptData.length === 0 && <p className="empty-state">No speech detected yet from tab audio.</p>}
        <div className="transcript-entries">
          {transcriptData.map((entry, index) => (
            <div key={index} className="transcript-entry">
              <div className="entry-header">
                <span className="timestamp">{entry.timestamp.toLocaleTimeString()}</span>
              </div>
              <div className="entry-text">{entry.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Main component
function TabAudioTranscription() {
  const [isCapturing, setIsCapturing] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [fullTranscript, setFullTranscript] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Refs
  const recognitionRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const lastProcessedTextRef = useRef('');
  const processingTimeoutRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech Recognition API not supported in this browser. Try Chrome or Edge.');
      return;
    }

    // Clean up function
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  // Start tab audio capture
  const startTabCapture = async () => {
    try {
      // Request screen sharing with audio
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true
      });

      // Check if audio is included
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        alert("No audio track found. Please make sure to select 'Share audio' when sharing the tab.");
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      // Store the stream for later cleanup
      mediaStreamRef.current = stream;
      
      // Set up audio context for processing tab audio only
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      sourceNodeRef.current = audioContextRef.current.createMediaStreamSource(stream);
      
      // Initialize speech recognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      // Connect the audio source to the speech recognition
      // This is a conceptual connection - the Web Speech API doesn't directly 
      // support connecting to an audio source, but we're setting up the environment
      // so that only tab audio is captured
      
      // Set up recognition handlers
      setupRecognitionHandlers();

      // Start recognition
      recognitionRef.current.start();
      setIsCapturing(true);

      // Handle stream ending
      stream.getVideoTracks()[0].onended = () => {
        stopTabCapture();
      };

    } catch (error) {
      console.error("Error capturing tab audio:", error);
      alert("Failed to capture tab audio: " + error.message);
    }
  };

  // Stop tab audio capture
  const stopTabCapture = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }

    setIsCapturing(false);

    // Add any remaining interim transcript to the full transcript
    if (interimTranscript.trim()) {
      setFullTranscript(prev => [
        ...prev,
        {
          text: interimTranscript.trim(),
          timestamp: new Date()
        }
      ]);
      setInterimTranscript('');
    }
  };

  // Process text to determine if it's a question or statement that needs AI response
  const processTextForAI = (text) => {
    // Skip if already processing or if text is too short
    if (isProcessing || text.length < 5) return;
    
    // Skip if this text has already been processed
    if (text === lastProcessedTextRef.current) return;
    
    // Clear any existing timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }
    
    // Set a timeout to process the text after a brief pause
    // This helps collect complete thoughts before sending to AI
    processingTimeoutRef.current = setTimeout(() => {
      // Check if the text contains a question or appears to need a response
      const isQuestion = text.includes('?') || 
                         /^(what|how|why|when|where|who|can|could|would|should|is|are|do|does|did|will|has|have)/.test(text.toLowerCase());
      
      if (isQuestion) {
        sendToGPT(text);
        lastProcessedTextRef.current = text;
      }
    }, 1000);
  };

  // Send text to GPT for processing
  const sendToGPT = async (text) => {
    // Skip if empty text
    if (!text.trim()) return;
    
    setIsProcessing(true);
    
    // Add user message to chat
    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    
    // Add loading message
    const loadingId = Date.now();
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: '',
      isLoading: true,
      id: loadingId,
      timestamp: new Date()
    }]);
    
    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (!apiKey) {
        throw new Error("OpenAI API key not found. Please add it to your .env file as REACT_APP_OPENAI_API_KEY.");
      }
      
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: "gpt-4.1-nano",
          messages: [
            { 
              role: "system", 
              content: "You are an AI assistant responding to questions and statements from a transcribed audio stream. Keep your responses concise, helpful, and conversational. If the input is a question, provide a direct answer. If it's a statement that doesn't require a response, you can acknowledge it briefly or provide relevant information. Limit responses to 3-5 sentences maximum." 
            },
            { role: "user", content: text }
          ],
          stream: true
        })
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      
      let result = "";
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter(line => line.trim().startsWith("data:"));
        
        for (const line of lines) {
          const data = line.replace(/^data: /, '');
          if (data === "[DONE]") break;
          
          try {
            const json = JSON.parse(data);
            const content = json.choices?.[0]?.delta?.content;
            if (content) {
              result += content;
              // Update the loading message with the content received so far
              // eslint-disable-next-line
              setChatMessages(prev => prev.map(msg => 
                msg.id === loadingId 
                  ? { ...msg, content: result, isLoading: false } 
                  : msg
              ));
            }
          } catch (e) {
            console.error("Error parsing JSON:", e);
          }
        }
      }
      
      // Ensure the loading state is removed
      setChatMessages(prev => prev.map(msg => 
        msg.id === loadingId 
          ? { ...msg, isLoading: false } 
          : msg
      ));
      
    } catch (error) {
      console.error("Error sending to GPT:", error);
      
      // Update the loading message with the error
      setChatMessages(prev => prev.map(msg => 
        msg.id === loadingId 
          ? { 
              ...msg, 
              content: `Error: ${error.message}. Please check your API key and try again.`, 
              isLoading: false,
              isError: true
            } 
          : msg
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  // Set up recognition event handlers
  const setupRecognitionHandlers = () => {
    if (!recognitionRef.current) return;

    recognitionRef.current.onstart = () => {
      setIsCapturing(true);
    };

    recognitionRef.current.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }

      if (interim) setInterimTranscript(interim);

      if (final) {
        // Add to full transcript
        const finalText = final.trim();
        setFullTranscript(prev => [
          ...prev,
          {
            text: finalText,
            timestamp: new Date()
          }
        ]);
        setInterimTranscript('');
        
        // Process the text for AI response
        processTextForAI(finalText);
      }
    };

    recognitionRef.current.onerror = (event) => {
      console.error("Recognition error:", event.error);
      if (event.error !== 'no-speech') {
        stopTabCapture();
      }
    };

    recognitionRef.current.onend = () => {
      // Only set isCapturing to false if we're intentionally stopping
      if (!mediaStreamRef.current) {
        setIsCapturing(false);
      } else {
        // Restart if we're still supposed to be capturing
        try {
          recognitionRef.current.start();
        } catch (error) {
          console.error("Failed to restart recognition:", error);
          setIsCapturing(false);
        }
      }
    };
  };

  return (
    <div className="tab-audio-transcription">
      <div className="page-header">
        <h1>Tab Audio Transcription & AI Assistant</h1>
        <p className="description">
          This page captures and transcribes audio from a shared tab (YouTube, Google Meet, etc.)
          and uses AI to respond to questions detected in the audio. No audio is recorded.
        </p>
      </div>

      <Controls
        isCapturing={isCapturing}
        onStartCapture={startTabCapture}
        onStopCapture={stopTabCapture}
      />

      <div className="status-indicator">
        {isCapturing && (
          <div className="capturing-status">
            <span className="status-dot"></span>
            Capturing Tab Audio
          </div>
        )}
      </div>

      <LiveCaptions interimText={interimTranscript} />

      <div className="content-columns">
        <Transcript transcriptData={fullTranscript} />
        <ChatDisplay messages={chatMessages} />
      </div>
    </div>
  );
}

export default TabAudioTranscription;
